import { useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { Button, IconButton, Panel, PageHead, EmptyState } from "@/sentinel";

/** Multi-line field in the TextField style (the library has no textarea). */
function TextArea({ label, rows = 3, ...rest }) {
  const id = useId();
  return (
    <div className="sn-field">
      {label && <label className="sn-field-label" htmlFor={id}>{label}</label>}
      <div className="sn-input-wrap" style={{ height: "auto", alignItems: "stretch" }}>
        <textarea id={id} rows={rows} className="sn-input" style={{ padding: "var(--space-2)", lineHeight: "20px", resize: "vertical" }} {...rest} />
      </div>
    </div>
  );
}

function Section({ s, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(s.body);

  const save = async () => {
    try { await api.pmUpdateContext(s.id, { body }); setEditing(false); onSaved(); toast.success("Saved."); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <Panel title={s.title} icon="BookOpen" data-testid={`pm-context-${s.slug}`}
      actions={canEdit && !editing ? <IconButton icon="Pencil" label={`Edit ${s.title}`} size="sm" onClick={() => setEditing(true)} /> : null}>
      {editing ? (
        <div className="sn-stack">
          <TextArea label={`Edit ${s.title}`} value={body} onChange={(e) => setBody(e.target.value)} rows={6} data-testid="pm-context-edit" />
          <div className="sn-row">
            <Button variant="primary" size="sm" icon="Check" onClick={save}>Save</Button>
            <Button variant="ghost" size="sm" onClick={() => { setBody(s.body); setEditing(false); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <p className="sn-muted" style={{ margin: 0, fontSize: 13, whiteSpace: "pre-wrap" }}>{s.body}</p>
      )}
    </Panel>
  );
}

export default function Context() {
  const { can } = useAuth();
  const canEdit = can("analyst");
  const { data: rows = [], refetch } = useQuery({ queryKey: ["pm-context"], queryFn: api.pmContext });

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="pm-context">
      <PageHead
        crumb={["Project", "Handoff context"]}
        title="Context and handoff"
        context="Living project memory. A fresh chat or teammate should read this top to bottom to resume where the project left off."
      />
      {rows.length === 0
        ? <EmptyState icon="Handshake" title="No context sections yet" />
        : rows.map((s) => <Section key={s.id} s={s} canEdit={canEdit} onSaved={refetch} />)}
    </div>
  );
}
